/**
 * iOS Destination Provider — UIDocumentPicker / security-scoped URLs.
 *
 * Uses UIDocumentPickerViewController in export mode via a native module.
 * Returns a security-scoped URL that must be resolved before use.
 *
 * Default: app Documents directory (visible in iOS Files app).
 */

import type { PlatformDestinationProvider } from './destination';
import type { TorrentDestination } from '../networking/engines/torrent/torrent-engine';

const NativeModules = (globalThis as any).NativeModules || {};
const { ShareDocumentPicker, SharePaths } = NativeModules;

export class IOSDestinationProvider implements PlatformDestinationProvider {
  async chooseDestination(
    suggestedFileName: string
  ): Promise<TorrentDestination> {
    if (!ShareDocumentPicker) {
      return this.defaultDestination(suggestedFileName);
    }

    // UIDocumentPickerViewController in export/create mode
    // Returns a security-scoped file path (already resolved by native)
    const path: string = await ShareDocumentPicker.pickExportDestination(
      suggestedFileName
    );
    return { kind: 'native-path', path };
  }

  async defaultDestination(
    suggestedFileName: string
  ): Promise<TorrentDestination> {
    if (SharePaths) {
      // App Documents directory — visible in Files app
      const docs: string = await SharePaths.getDocumentsDirectory();
      return { kind: 'native-path', path: `${docs}/Share/${suggestedFileName}` };
    }

    // Absolute fallback (best-guess — correct in most iOS setups)
    return {
      kind: 'native-path',
      path: `./Documents/Share/${suggestedFileName}`,
    };
  }
}
